// src/contexts/NavigationContext.js
import React, { createContext, useContext, useState, useCallback } from 'react';

const NavigationContext = createContext();

// Custom hook to use navigation context
export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

// Valid admin pages
const VALID_ADMIN_PAGES = [
  'dashboard',
  'liveMap',
  'createJob',
  'clients',
  'engineers',
  'bulkPortal',
  'designer',
  'invoices',
  'settings',
  'jobRequests',
];

// Page labels for breadcrumbs
const PAGE_LABELS = {
  dashboard: 'Dashboard',
  liveMap: 'Live Map',
  createJob: 'Create Job',
  clients: 'Clients',
  engineers: 'Engineers',
  bulkPortal: 'Bulk Portal',
  designer: 'Job Designer',
  invoices: 'Invoices',
  settings: 'Settings',
  jobRequests: 'Job Requests',
};

export const NavigationProvider = ({ children }) => {
  // Admin navigation state
  const [adminPage, setAdminPage] = useState('dashboard');
  
  // Selected items state
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedEngineerId, setSelectedEngineerId] = useState(null);

  // Data for pre-filling create job page from a request
  const [createJobData, setCreateJobData] = useState(null);

  // Additional context data (for displaying names, etc.)
  const [selectedJobData, setSelectedJobData] = useState(null);
  const [selectedClientData, setSelectedClientData] = useState(null);
  const [selectedEngineerData, setSelectedEngineerData] = useState(null);

  // Navigation history for breadcrumbs
  const [navigationHistory, setNavigationHistory] = useState([
    { label: 'Dashboard', page: 'dashboard' }
  ]);

  // -----------------------------------------------
  // ADMIN PAGE NAVIGATION
  // -----------------------------------------------
  const navigateToAdminPage = useCallback((page) => {
    if (!VALID_ADMIN_PAGES.includes(page)) {
      console.warn(`Invalid admin page: ${page}`);
      return false;
    }

    setAdminPage(page);
    
    // Clear selected items when changing pages
    setSelectedJobId(null);
    setSelectedClientId(null);
    setSelectedEngineerId(null);
    setSelectedJobData(null);
    setSelectedClientData(null);
    setSelectedEngineerData(null);

    // Clear create job data if we navigate away from it
    if (page !== 'createJob') {
      setCreateJobData(null);
    }

    // Update navigation history
    setNavigationHistory([{ label: PAGE_LABELS[page] || page, page }]);
    
    return true;
  }, []);

  const navigateToCreateJobWithData = useCallback(
    data => {
      setCreateJobData(data);
      navigateToAdminPage('createJob');
    },
    [navigateToAdminPage]
  );
  // -----------------------------------------------
  // JOB SELECTION
  // -----------------------------------------------
  const selectJob = useCallback((jobId, jobData = null) => {
    setSelectedJobId(jobId);
    setSelectedJobData(jobData);
    
    // Add to navigation history
    setNavigationHistory(prev => [
      ...prev,
      { label: jobData?.jobTemplateName || `Job ${jobId}`, type: 'job', id: jobId }
    ]);
  }, []);

  const clearJobSelection = useCallback(() => {
    setSelectedJobId(null);
    setSelectedJobData(null);
    
    // Remove job from history
    setNavigationHistory(prev => prev.filter(item => item.type !== 'job'));
  }, []);

  // -----------------------------------------------
  // CLIENT SELECTION
  // -----------------------------------------------
  const selectClient = useCallback((clientId, clientData = null) => {
    setSelectedClientId(clientId);
    setSelectedClientData(clientData);
    
    // Add to navigation history
    setNavigationHistory(prev => [
      ...prev,
      { label: clientData?.name || `Client ${clientId}`, type: 'client', id: clientId }
    ]);
  }, []);

  const clearClientSelection = useCallback(() => {
    setSelectedClientId(null);
    setSelectedClientData(null);
    
    // Remove client from history
    setNavigationHistory(prev => prev.filter(item => item.type !== 'client'));
  }, []);

  // -----------------------------------------------
  // ENGINEER SELECTION
  // -----------------------------------------------
  const selectEngineer = useCallback((engineerId, engineerData = null) => {
    setSelectedEngineerId(engineerId);
    setSelectedEngineerData(engineerData);
    
    // Add to navigation history
    setNavigationHistory(prev => [
      ...prev,
      { label: engineerData?.name || `Engineer ${engineerId}`, type: 'engineer', id: engineerId }
    ]);
  }, []);

  const clearEngineerSelection = useCallback(() => {
    setSelectedEngineerId(null);
    setSelectedEngineerData(null);
    
    // Remove engineer from history
    setNavigationHistory(prev => prev.filter(item => item.type !== 'engineer'));
  }, []);

  // -----------------------------------------------
  // GO BACK NAVIGATION
  // -----------------------------------------------
  const goBack = useCallback(() => {
    // Clear the most recent selection
    if (selectedJobId) {
      clearJobSelection();
    } else if (selectedClientId) {
      clearClientSelection();
    } else if (selectedEngineerId) {
      clearEngineerSelection();
    } else {
      // Already at main page, go to dashboard
      navigateToAdminPage('dashboard');
    }
  }, [
    selectedJobId,
    selectedClientId,
    selectedEngineerId,
    clearJobSelection,
    clearClientSelection,
    clearEngineerSelection,
    navigateToAdminPage,
  ]);

  // -----------------------------------------------
  // RESET ALL NAVIGATION
  // -----------------------------------------------
  const resetNavigation = useCallback(() => {
    setAdminPage('dashboard');
    setSelectedJobId(null);
    setSelectedClientId(null);
    setSelectedEngineerId(null);
    setSelectedJobData(null);
    setSelectedClientData(null);
    setSelectedEngineerData(null);
    setNavigationHistory([{ label: 'Dashboard', page: 'dashboard' }]);
  }, []);

  // -----------------------------------------------
  // HELPER FUNCTIONS
  // -----------------------------------------------
  const isViewingDetail = useCallback(() => {
    return !!(selectedJobId || selectedClientId || selectedEngineerId);
  }, [selectedJobId, selectedClientId, selectedEngineerId]);

  const getCurrentView = useCallback(() => {
    if (selectedJobId) return 'job-detail';
    if (selectedClientId) return 'client-detail';
    if (selectedEngineerId) return 'engineer-detail';
    return adminPage;
  }, [selectedJobId, selectedClientId, selectedEngineerId, adminPage]);

  // Context value
  const value = {
    // Admin page state
    adminPage,
    navigateToAdminPage,

    // Job selection
    createJobData,
    navigateToCreateJobWithData,

    // Job selection
    selectedJobId,
    selectedJobData,
    selectJob,
    clearJobSelection,

    // Client selection
    selectedClientId,
    selectedClientData,
    selectClient,
    clearClientSelection,

    // Engineer selection
    selectedEngineerId,
    selectedEngineerData,
    selectEngineer,
    clearEngineerSelection,

    // Navigation
    navigationHistory,
    goBack,
    resetNavigation,

    // Helpers
    isViewingDetail,
    getCurrentView,
    validAdminPages: VALID_ADMIN_PAGES,
    pageLabels: PAGE_LABELS,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export default NavigationContext;